import { useCallback, useMemo, useState } from 'react';

import {
  LAYOUT_PREFERENCE_STORAGE_KEY,
  TABLET_MENU_ITEM_STORAGE_KEY,
  TABLET_MENU_OPEN_STORAGE_KEY,
} from './constants';
import { LayoutPreferenceContext } from './LayoutPreferenceContext';
import {
  getStoredLayoutPreference,
  StoredLayoutPreference,
} from './layoutPreferenceUtils';

export const LayoutPreferenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [layoutPreference, setLayoutPreference] =
    useState<StoredLayoutPreference>(getStoredLayoutPreference);

  const persistLayoutPreference = useCallback(
    (nextPreference: StoredLayoutPreference) => {
      setLayoutPreference(nextPreference);
      localStorage.setItem(
        LAYOUT_PREFERENCE_STORAGE_KEY,
        JSON.stringify(nextPreference),
      );
      localStorage.removeItem(TABLET_MENU_OPEN_STORAGE_KEY);
      localStorage.removeItem(TABLET_MENU_ITEM_STORAGE_KEY);
    },
    [],
  );

  const setIsMenuPinnedOpen = useCallback(
    (nextValue: boolean) => {
      persistLayoutPreference({
        ...layoutPreference,
        isMenuPinnedOpen: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const setIsTabletMenuOpen = useCallback(
    (nextValue: boolean) => {
      persistLayoutPreference({
        ...layoutPreference,
        isTabletMenuOpen: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const setTabletMenuItemId = useCallback(
    (nextValue: string | null) => {
      persistLayoutPreference({
        ...layoutPreference,
        tabletMenuItemId: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const value = useMemo(
    () => ({
      isMenuPinnedOpen: layoutPreference.isMenuPinnedOpen,
      isTabletMenuOpen: layoutPreference.isTabletMenuOpen,
      setIsMenuPinnedOpen,
      setIsTabletMenuOpen,
      setTabletMenuItemId,
      tabletMenuItemId: layoutPreference.tabletMenuItemId,
    }),
    [
      layoutPreference.isMenuPinnedOpen,
      layoutPreference.isTabletMenuOpen,
      layoutPreference.tabletMenuItemId,
      setIsMenuPinnedOpen,
      setIsTabletMenuOpen,
      setTabletMenuItemId,
    ],
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
    </LayoutPreferenceContext.Provider>
  );
};
